import { useQuery, type UseQueryOptions, type UseQueryResult, type QueryKey, type QueryFunctionContext } from "@tanstack/react-query";

import { useErrorTranslation } from "@/shared/hooks/useErrorTranslation";
import { getErrorMessageKey, isNetworkError } from "@/shared/lib/errorUtils";
import { captureException } from "@/shared/lib/sentry";

export type NormalizedSupabaseError = Error & {
  translationKey: string;
  fallback: string;
  originalError: unknown;
};

const normalizeSupabaseError = (error: unknown): NormalizedSupabaseError => {
  const { key, fallback } = getErrorMessageKey(error);
  const normalizedError = new Error(fallback) as NormalizedSupabaseError;
  normalizedError.translationKey = key;
  normalizedError.fallback = fallback;
  normalizedError.originalError = error;
  return normalizedError;
};

type UseSupabaseQueryOptions<
  TQueryFnData,
  TData = TQueryFnData,
  TError = NormalizedSupabaseError,
  TQueryKey extends QueryKey = QueryKey
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn' | 'queryKey'> & {
  /**
   * Optional namespaces to ensure feature-specific translations are available.
   * Defaults to ['ui'] which contains the shared error dictionary.
   */
  namespaces?: string | string[];
  /**
   * Optional predicate to skip Sentry capture for expected, non-fault errors
   * (e.g. an expired invite link the user clicked). Returning true suppresses
   * `captureException` for that error; normalization/throwing is unchanged.
   * Defaults to capturing every error.
   */
  skipCapture?: (error: unknown) => boolean;
};

/**
 * Shared query helper that enforces normalized, translated Supabase errors.
 * This keeps feature model hooks light while guaranteeing we never leak raw RLS/table names.
 *
 * @template TQueryFnData - The type returned by the queryFn
 * @template TData - The type after the optional select transformation (defaults to TQueryFnData)
 * @template TQueryKey - The query key type
 */
export function useSupabaseQuery<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: (context: QueryFunctionContext<TQueryKey>) => Promise<TQueryFnData>,
  options?: UseSupabaseQueryOptions<TQueryFnData, TData, NormalizedSupabaseError, TQueryKey>
): UseQueryResult<TData, NormalizedSupabaseError> {
  const { translateError } = useErrorTranslation(options?.namespaces ?? ["ui"]);
  const { skipCapture, ...queryOptions } = options ?? {};

  return useQuery<TQueryFnData, NormalizedSupabaseError, TData, TQueryKey>({
    ...queryOptions,
    queryKey,
    queryFn: async (context) => {
      try {
        return await queryFn(context);
      } catch (error) {
        // Capture the original error in Sentry before normalization, unless the
        // caller flags it as an expected, non-fault condition. Transient network
        // failures (offline, dropped fetch) are connectivity faults, not app
        // bugs — skip them so they don't drown out real errors.
        if (!isNetworkError(error) && !skipCapture?.(error)) {
          captureException(error, { source: "useSupabaseQuery", queryKey: JSON.stringify(queryKey) });
        }

        // Preserve custom error classes that have their own error handling
        // (e.g., AcceptInviteError with code/retryable properties)
        if (error instanceof Error && 'code' in error && 'retryable' in error) {
          throw error;
        }
        const normalized = normalizeSupabaseError(error);
        normalized.message = translateError(
          normalized.originalError ?? normalized
        );
        throw normalized;
      }
    },
  });
}

export { normalizeSupabaseError };
